const BRAND_SUFFIX = " | JamWith";
// Google cuts titles at about 60 characters. The suffix always stays intact.
const PROFILE_TITLE_MAX = 60;
const META_DESCRIPTION_MAX = 160;
const SITE_DESCRIPTION = "Find musicians, DJs, producers, and engineers to collaborate with. Browse profiles by skill and city on JamWith.";
const DEFAULT_OG_PATH = "/og-default.png";

function seoSlug(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function cleanText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clip(value, max) {
  const text = cleanText(value);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

function joinSkills(skills, limit = 3) {
  const names = [];
  const seen = new Set();
  (skills || []).forEach((skill) => {
    const name = cleanText(typeof skill === "string" ? skill : (skill?.displayName || skill?.name));
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });
  const shown = names.slice(0, limit);
  if (shown.length === 0) return "";
  if (shown.length === 1) return shown[0];
  if (shown.length === 2) return `${shown[0]} and ${shown[1]}`;
  return `${shown[0]}, ${shown[1]} and ${shown[2]}`;
}

function clipWords(text, max) {
  if (text.length <= max) return text;
  const room = Math.max(1, max - 1);
  let sliced = text.slice(0, room).trimEnd();
  const cutMidWord = room < text.length && text[room] !== " ";
  if (cutMidWord) {
    const space = sliced.lastIndexOf(" ");
    if (space >= Math.floor(room * 0.6)) sliced = sliced.slice(0, space).trimEnd();
  }
  return `${sliced}…`;
}

function clippedBareTitle(name) {
  const bare = `${name}${BRAND_SUFFIX}`;
  if (bare.length <= PROFILE_TITLE_MAX) return bare;
  const budget = PROFILE_TITLE_MAX - BRAND_SUFFIX.length;
  return `${clipWords(name, budget)}${BRAND_SUFFIX}`;
}

function profileTitle({ displayName, skills, city, headline }) {
  const name = cleanText(displayName);
  if (!name) return `Profile${BRAND_SUFFIX}`;
  const roles = joinSkills(skills);
  const place = cleanText(city);
  let detail = "";
  if (roles && place) detail = `${roles} in ${place}`;
  else if (roles) detail = roles;
  else if (place) detail = place;
  else detail = cleanText(headline);
  if (!detail) return clippedBareTitle(name);
  const full = `${name} - ${detail}${BRAND_SUFFIX}`;
  if (full.length <= PROFILE_TITLE_MAX) return full;
  const budget = PROFILE_TITLE_MAX - name.length - BRAND_SUFFIX.length - 3;
  if (budget < 12) return clippedBareTitle(name);
  return `${name} - ${clipWords(detail, budget)}${BRAND_SUFFIX}`;
}

function imageAlt(displayName, skills, city) {
  const name = cleanText(displayName);
  const roles = joinSkills(skills, 2);
  const place = cleanText(city);
  if (name && roles && place) return `${name}, ${roles} in ${place}`;
  if (name && roles) return `${name}, ${roles}`;
  if (name && place) return `${name} in ${place}`;
  return name;
}

function placeSlug(location) {
  const city = cleanText(location?.city);
  if (!city) return "";
  return seoSlug([city, cleanText(location?.region), cleanText(location?.country)].filter(Boolean).join(" "));
}

function placeLabel(location) {
  const city = cleanText(location?.city);
  const region = cleanText(location?.region);
  const country = cleanText(location?.country);
  const composed = [city, region, country].filter(Boolean).join(", ");
  const display = cleanText(location?.displayText);
  if (display && (display.includes(",") || !composed)) return display;
  return composed || display;
}

function metaDescription({ displayName, skills, city, description }) {
  const name = cleanText(displayName) || "This musician";
  const bio = clip(description, 140);
  if (bio) {
    const withName = bio.toLowerCase().includes(name.toLowerCase()) ? bio : `${name}. ${bio}`;
    return clip(withName, META_DESCRIPTION_MAX);
  }
  const roles = joinSkills(skills);
  const place = cleanText(city);
  let who = name;
  if (roles && place) who = `${name} is a ${roles} in ${place}`;
  else if (roles) who = `${name} is a ${roles}`;
  else if (place) who = `${name} is a musician in ${place}`;
  return clip(`${who} on JamWith.`, META_DESCRIPTION_MAX);
}

module.exports = {
  BRAND_SUFFIX,
  PROFILE_TITLE_MAX,
  META_DESCRIPTION_MAX,
  SITE_DESCRIPTION,
  DEFAULT_OG_PATH,
  seoSlug,
  cleanText,
  clip,
  joinSkills,
  profileTitle,
  imageAlt,
  placeSlug,
  placeLabel,
  metaDescription,
};
